import type { GooglePlace, Supplier } from "@albertine/types";
import type {
    CatalogItem as CatalogItemApi,
    CatalogItemFull,
} from "@albertine/types/openapi/lmt";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { CatalogItem } from "../../common/CatalogItem";
import {
    googlePlaceService,
    photoUrlOptions,
} from "../../common/utils/google/GooglePlaceService";
import { GooglePlaceFieldsNewCatalogItem } from "../../common/utils/google/place.util";
import { singularPlural } from "../../common/utils/time.formatted.util";
import config from "../../config";
import { useGoogleMapsScript } from "../../useScript";
import { useComponentState } from "../../utils/customHook.util";
import Asset from "../Common/Asset";
import Button from "../Common/Buttons";
import { Flex, FlexInline } from "../Common/Flex";
import Icon, { PictogramIcon } from "../Common/Icon";
import Label from "../Common/Label";
import FullScreenOverlay from "../Common/Overlay/FullScreenOverlay";
import ModalOverlay from "../Common/Overlay/ModalOverlay";
import Tag from "../Common/Tag";
import GooglePlaceInputField from "../GooglePlaceInputField/GooglePlaceInputField";
import "./CatalogItemCreate.css";
import { useGet } from "@albertine/api-client";
import type { CatalogSourceKey } from "../../common/CatalogCategories";
import { isTypeOf } from "../../common/utils/zod.util";
import Hr from "../Common/Hr";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import ArticleThumbnail from "./ArticleThumbnail";
import CatalogItemForm from "./CatalogItemForm";
import BusinessStatus from "./GooglePlace/BusinessStatus";
import GoogleReview from "./GooglePlace/GoogleReview";

function useFindPossibleDuplicates(googlePlace: GooglePlace | undefined) {
    const { data: byGooglePlace, isLoading: isLoadingByGooglePlace } = useGet(
        "catalogItemsSearch",
        {
            query: {
                googlePlaceId: googlePlace?.id || "",
                categories: [],
                ids: [],
                supplierLmtIds: [],
                sources: [],
                tags: [],
                pageSize: "100",
            },
        },
        {
            enabled: !!googlePlace?.id,
        },
    );

    const hasLocationFilter =
        googlePlace?.location?.latitude !== undefined &&
        googlePlace?.location?.longitude !== undefined;

    const { data: byLocation, isLoading: isLoadingByLocation } = useGet(
        "catalogItemsSearch",
        {
            query: {
                latitude: googlePlace?.location?.latitude.toString(),
                longitude: googlePlace?.location?.longitude.toString(),
                maxGeoDistanceKm: "0.5",
                nameSearchTerm: googlePlace?.displayName?.text ?? undefined,
                categories: [],
                ids: [],
                supplierLmtIds: [],
                sources: [],
                tags: [],
                pageSize: "20",
            },
        },
        {
            enabled: hasLocationFilter && !!googlePlace?.displayName?.text,
        },
    );

    const itemsByGooglePlace = byGooglePlace?.catalogItems ?? [];
    const itemsByLocation = byLocation?.catalogItems ?? [];
    const allItems = [...itemsByGooglePlace];
    itemsByLocation.forEach((item) => {
        if (!allItems.some((i) => i.id === item.id)) {
            allItems.push(item);
        }
    });
    return {
        data: allItems,
        isLoading: isLoadingByGooglePlace || isLoadingByLocation,
    };
}

function DuplicateSearchStatus(props: {
    isSearching: boolean;
    duplicates: CatalogItemApi[] | undefined;
}) {
    const { isSearching, duplicates } = props;
    if (isSearching) {
        return (
            <Flex.Row gap="medium">
                <LoadingSpinner />
                <h5>Duplicate check running; please wait</h5>
            </Flex.Row>
        );
    }

    if (duplicates?.length !== 0) {
        return (
            <Flex.Row gap="medium">
                <PictogramIcon.PriorityHigh.Small.S90 />
                <h5>
                    This might be a duplicate; please check these similar items before
                    continuing
                </h5>
            </Flex.Row>
        );
    }

    return (
        <Flex.Row gap="medium">
            <PictogramIcon.Check.Small.P90 />
            <h5>No duplicates found</h5>
        </Flex.Row>
    );
}

type FindFromGooglePlacesProps = {
    returnToSearch: () => void;
    onDuplicateSelect: (catalogItemId: string) => void;
    onContinue: (selectedGooglePlace: GooglePlace | undefined) => void;
    initialPlaceId?: string;
};

type FindFromGooglePlacesState = {
    googlePlace: GooglePlace | undefined;
};

function FindFromGooglePlaces(props: FindFromGooglePlacesProps) {
    const { onContinue, onDuplicateSelect, returnToSearch, initialPlaceId } = props;
    const { state, setState } = useComponentState<FindFromGooglePlacesState>({
        googlePlace: undefined,
    });

    const [photoUri, setPhotoUri] = useState<string | undefined>(undefined);
    const googleService = googlePlaceService(config.mapsApiKey);
    const scriptReady = useGoogleMapsScript() === "ready";
    const hasAutoSelectedRef = useRef(false);

    const duplicatesQuery = useFindPossibleDuplicates(state.googlePlace);

    function selectPlace(googlePlace: GooglePlace) {
        fetchGooglePhoto(googlePlace);
        setState({ googlePlace });
    }

    const autoSelectInitialPlace = useEffectEvent((placeId: string) => {
        void googleService
            .fetchGooglePlace(placeId, GooglePlaceFieldsNewCatalogItem)
            .then((googlePlace) => {
                if (googlePlace) {
                    selectPlace(googlePlace);
                }
            });
    });

    useEffect(() => {
        if (hasAutoSelectedRef.current || !scriptReady || !initialPlaceId) {
            return;
        }
        hasAutoSelectedRef.current = true;
        autoSelectInitialPlace(initialPlaceId);
    }, [scriptReady, initialPlaceId]);

    async function fetchGooglePhoto(googlePlace: GooglePlace) {
        const populated = await googleService.populatePlacePhotoUris(
            googlePlace,
            { maxHeightPx: 200, maxWidthPx: 200 },
            1,
        );
        setPhotoUri(populated.photos?.[0]?.photoUri ?? undefined);
    }

    async function onAddToCatalog() {
        if (!state.googlePlace) return;
        if (!config.mapsApiKey) return;

        if (state.googlePlace?.photos && state.googlePlace.photos.length > 0) {
            onContinue(
                await googleService.populatePlacePhotoUris(
                    state.googlePlace,
                    photoUrlOptions,
                ),
            );
        } else {
            onContinue(state.googlePlace);
        }
    }

    return (
        <ModalOverlay.Base title="Add new" onClose={returnToSearch}>
            <ModalOverlay.Content>
                <Flex.Column
                    className="catalog-item-create__find-from-google-content"
                    gap="xlarge"
                    alignItems="center"
                >
                    {!state.googlePlace && <h3>Find from Google Places</h3>}

                    <GooglePlaceInputField
                        label="Place name"
                        fields={GooglePlaceFieldsNewCatalogItem}
                        onSelect={(googlePlace) => {
                            selectPlace(googlePlace);
                        }}
                        onClear={() => {
                            setState({
                                googlePlace: undefined,
                            });
                        }}
                    />

                    {state.googlePlace && (
                        <Flex.Column>
                            <Flex.Row gap="xlarge" alignItems="start">
                                <div className="catalog-item-create__find-from-google-thumbnail">
                                    {state.googlePlace.photos && (
                                        <>
                                            <Asset
                                                className="catalog-item-create__find-from-google-thumbnail-image"
                                                src={photoUri || ""}
                                            />
                                            <Tag.Small.Bold.T90 className="catalog-item-create__find-from-google-thumbnail-image-count">
                                                {singularPlural(
                                                    `${state.googlePlace.photos.length} image`,
                                                    `${state.googlePlace.photos.length} images`,
                                                    state.googlePlace.photos.length,
                                                )}
                                            </Tag.Small.Bold.T90>
                                        </>
                                    )}
                                </div>
                                <Flex.Column
                                    gap="large"
                                    className="catalog-item-create__find-from-google-details"
                                >
                                    <FlexInline gap="medium" alignSelf="start">
                                        <h4>{state.googlePlace.displayName?.text}</h4>
                                        <BusinessStatus
                                            businessStatus={
                                                state.googlePlace.businessStatus
                                            }
                                        />
                                    </FlexInline>

                                    <Flex.Column gap="medium">
                                        <Flex.Row gap="medium">
                                            <Icon.Call.Medium.N10 />
                                            {state.googlePlace
                                                .internationalPhoneNumber ? (
                                                <Label.Medium.SemiBold.N10>
                                                    {
                                                        state.googlePlace
                                                            .internationalPhoneNumber
                                                    }
                                                </Label.Medium.SemiBold.N10>
                                            ) : (
                                                <Label.Medium.SemiBold.N60>
                                                    phone unavailable
                                                </Label.Medium.SemiBold.N60>
                                            )}
                                        </Flex.Row>

                                        <Flex.Row gap="medium">
                                            <Icon.Link.Medium.N10 />
                                            {state.googlePlace.websiteUri ? (
                                                <a
                                                    href={state.googlePlace.websiteUri}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <Label.Medium.SemiBold.N10>
                                                        {state.googlePlace.websiteUri}
                                                    </Label.Medium.SemiBold.N10>
                                                </a>
                                            ) : (
                                                <Label.Medium.SemiBold.N60>
                                                    website unavailable
                                                </Label.Medium.SemiBold.N60>
                                            )}
                                        </Flex.Row>

                                        <Flex.Row gap="medium">
                                            <Icon.LocationOn.Medium.N10 />

                                            {state.googlePlace.googleMapsUri &&
                                            state.googlePlace.formattedAddress ? (
                                                <a
                                                    href={
                                                        state.googlePlace.googleMapsUri
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <Label.Medium.SemiBold.N10>
                                                        {
                                                            state.googlePlace
                                                                .formattedAddress
                                                        }
                                                    </Label.Medium.SemiBold.N10>
                                                </a>
                                            ) : (
                                                <Label.Medium.SemiBold.N60>
                                                    address unavailable
                                                </Label.Medium.SemiBold.N60>
                                            )}
                                        </Flex.Row>

                                        {state.googlePlace.id && (
                                            <Flex.Row gap="medium">
                                                <Icon.StarRate.Medium.N10 />
                                                <GoogleReview
                                                    label="Google"
                                                    reviews={{
                                                        rating: state.googlePlace
                                                            .rating,
                                                        reviewsTotal:
                                                            state.googlePlace
                                                                .userRatingCount,
                                                        googlePlaceId:
                                                            state.googlePlace.id,
                                                    }}
                                                />
                                            </Flex.Row>
                                        )}
                                    </Flex.Column>
                                </Flex.Column>
                            </Flex.Row>

                            <Flex.Column gap="large">
                                <Hr />

                                <DuplicateSearchStatus
                                    duplicates={duplicatesQuery.data}
                                    isSearching={duplicatesQuery.isLoading}
                                />
                                {duplicatesQuery.data &&
                                    duplicatesQuery.data.length !== 0 && (
                                        <Flex.Column gap="medium">
                                            {duplicatesQuery.data.map(
                                                (duplicateArticle) => {
                                                    const matchText = isTypeOf(
                                                        duplicateArticle,
                                                        CatalogItem,
                                                    )
                                                        ? "Matching Google place"
                                                        : "Nearby with similar name";
                                                    return (
                                                        <Flex.Row
                                                            key={duplicateArticle.lmtId}
                                                            className="catalog-item-create__duplicate-container"
                                                            alignItems="start"
                                                            onClick={() => {
                                                                onDuplicateSelect(
                                                                    duplicateArticle.lmtId,
                                                                );
                                                            }}
                                                        >
                                                            <ArticleThumbnail
                                                                article={
                                                                    duplicateArticle
                                                                }
                                                                className="catalog-item-create__duplicate-article"
                                                                locationFormat="address"
                                                            />

                                                            <Flex.Row
                                                                justifyContent="space-between"
                                                                className="catalog-item-create__duplicate-match-details"
                                                            >
                                                                <Tag.Small.S90>
                                                                    {matchText}
                                                                </Tag.Small.S90>
                                                                <Icon.ArrowForward.Medium.N10 />
                                                            </Flex.Row>
                                                        </Flex.Row>
                                                    );
                                                },
                                            )}
                                        </Flex.Column>
                                    )}
                            </Flex.Column>
                        </Flex.Column>
                    )}
                </Flex.Column>
            </ModalOverlay.Content>
            <ModalOverlay.Footer>
                <Flex.Column
                    gap="xlarge"
                    alignItems="center"
                    className="catalog-item-create__find-from-google-actions"
                >
                    {state.googlePlace ? (
                        <Button.N10
                            label="Add to catalog"
                            onClick={() => onAddToCatalog()}
                        />
                    ) : (
                        <>
                            <Label.Medium.SemiBold>
                                Or start from a blank slate
                            </Label.Medium.SemiBold>
                            <Button.N10
                                label="Create from scratch"
                                onClick={() => {
                                    onContinue(undefined);
                                }}
                            />
                        </>
                    )}
                </Flex.Column>
            </ModalOverlay.Footer>
        </ModalOverlay.Base>
    );
}

type CreateNewItemProps = {
    onCancel: () => void;
    onSave: (addedItem: CatalogItemFull) => void;
    isAdmin: boolean;
    suppliers: Supplier[];
    googlePlace: GooglePlace | undefined;
    initialValues?: Partial<CatalogItemFull>;
    defaultCatalogSource?: CatalogSourceKey;
};

function CreateNewItem({
    onCancel,
    onSave,
    googlePlace,
    initialValues,
    isAdmin,
    suppliers,
    defaultCatalogSource = "unknown",
}: CreateNewItemProps) {
    return (
        <FullScreenOverlay>
            <Flex.Row gap="large" className="catalog-item-create__title">
                <h2>New catalog item</h2>
            </Flex.Row>
            <CatalogItemForm
                catalogItem={undefined}
                googlePlace={googlePlace}
                initialValues={initialValues}
                isAdmin={isAdmin}
                suppliers={suppliers}
                defaultCatalogSource={defaultCatalogSource}
                onCancel={() => {
                    if (window.confirm("Discard changes?")) {
                        onCancel();
                    }
                }}
                onSave={onSave}
            />
        </FullScreenOverlay>
    );
}

type Props = {
    onCancel: () => void;
    onDuplicateSelect: (catalogItemId: string) => void;
    onSave: (addedItem: CatalogItemFull) => void;
    isAdmin: boolean;
    suppliers: Supplier[];
    defaultCatalogSource?: CatalogSourceKey;
    initialPlaceId?: string;
    initialValues?: Partial<CatalogItemFull>;
};

type State = {
    phase: "google_place" | "create";
    selectedGooglePlace: GooglePlace | undefined;
};

function CatalogItemCreate({
    onCancel,
    onDuplicateSelect,
    onSave,
    isAdmin,
    suppliers,
    defaultCatalogSource = "unknown",
    initialPlaceId,
    initialValues,
}: Props) {
    const { state, setState } = useComponentState<State>({
        phase: "google_place",
        selectedGooglePlace: undefined,
    });

    if (state.phase === "google_place") {
        return (
            <FindFromGooglePlaces
                returnToSearch={onCancel}
                onDuplicateSelect={onDuplicateSelect}
                initialPlaceId={initialPlaceId}
                onContinue={(selectedGooglePlace) => {
                    setState({
                        phase: "create",
                        selectedGooglePlace,
                    });
                }}
            />
        );
    }

    return (
        <CreateNewItem
            onCancel={onCancel}
            onSave={onSave}
            isAdmin={isAdmin}
            suppliers={suppliers}
            googlePlace={state.selectedGooglePlace}
            initialValues={initialValues}
            defaultCatalogSource={defaultCatalogSource}
        />
    );
}

export default CatalogItemCreate;
